/**
 * 캔버스 문서 형식 버전과 변환.
 *
 * 왜 필요한가: 저장된 캔버스에는 형식 표시가 없었다. 그래서 ① 예전 문서를 새 빌드가 읽을 때 무엇을 보정해야 하는지
 * 알 수 없고 ② 새 문서를 예전 빌드가 열어 저장하면 모르는 필드가 조용히 사라졌다. 스탠드얼론은 현장마다 빌드가
 * 달라 ②가 실제로 일어난다.
 *
 * 설계 두 가지:
 *
 * 1. **모양으로 판정한다.** 버전 숫자만 믿지 않는다. 지금까지 저장된 문서에는 버전이 아예 없어 전부 v0 이고,
 *    형식 필드를 모르는 예전 빌드가 다시 저장하면 최신 문서도 v0 으로 되돌아간다. 그래서 각 변환 단계는
 *    "이 모양이면 바꾼다" 로 쓰고, 이미 변환된 문서에 다시 돌려도 아무 일이 없게 한다(멱등).
 *
 * 2. **읽을 때 고쳐 쓰지 않는다.** 변환은 메모리에서만 하고 저장하지 않는다. 캔버스 저장은 덮어쓰기가 아니라
 *    버전을 쌓는 구조라(workflow_store.py), 사용자가 다음에 저장하는 순간 변환 결과가 새 버전으로 남고 원본은
 *    이전 버전으로 그대로 보존된다 — 따로 복제할 필요가 없다. 열기만 했는데 버전이 쌓이거나, 조회 전용
 *    세션에서 쓰기가 일어나는 일도 없다.
 *
 * 문서가 이 빌드보다 새 형식이면 변환하지 않는다. 모르는 형식을 추측해서 고치면 그 문서를 망가뜨린다 —
 * 대신 조회 전용으로 열고 화면이 알린다(workflowSlice.schemaBlock).
 */
import type { Node, Edge } from '@xyflow/react';
import i18n from '../i18n';

/**
 * 이 빌드가 쓰는 캔버스 형식 버전.
 *
 * 올리는 때: 저장된 캔버스를 예전 빌드가 **잘못 읽을** 변경을 했을 때만. 노드 타입 이름 변경, config 키의 의미
 * 변경, 필드 제거가 여기 해당한다. 필드를 **더하기만** 하는 변경은 올리지 않는다 — 예전 빌드는 모르는 키를
 * 무시하고 읽으므로 호환이 깨지지 않는다.
 * 올릴 때는 MIGRATIONS 에 단계를 하나 추가한다.
 */
export const CANVAS_SCHEMA_VERSION = 1;

/** 저장 문서의 형식 필드 이름 — 예전 빌드는 이 키를 모르고 무시한다(그래서 필드 추가 자체는 안전하다) */
export const SCHEMA_FIELD = 'schemaVersion';

export interface RawCanvasDoc {
  nodes?: Node[];
  edges?: Edge[];
  settings?: any;
  [SCHEMA_FIELD]?: number;
  [k: string]: unknown;
}

export interface MigrateResult {
  nodes: Node[];
  edges: Edge[];
  settings?: any;
  /** 문서에 적혀 있던 형식 버전. 없으면 0 */
  docVersion: number;
  /** 변환이 실제로 무언가를 바꿨는가 */
  migrated: boolean;
  /** 무엇을 바꿨는지 — 화면이 사용자에게 알린다 */
  notes: string[];
  /** 이 빌드보다 새 형식인가. true 면 변환하지 않았고 저장·배포를 막아야 한다 */
  tooNew: boolean;
  /** 구조를 바꾸는 변환이 끼어 있었는가 — 저장하면 되돌릴 수 없다는 것을 화면이 알린다 */
  needsClone: boolean;
}

interface Migration {
  /** 이 단계를 마치면 문서는 이 버전이 된다 */
  to: number;
  /** 사용자에게 보일 한 줄 */
  label: string;
  /**
   * 데이터 구조가 실제로 바뀌는가 (datamodeler modelSchema 의 lossless/structural 구분과 같다).
   *
   * false(lossless): 모양만 보정한다. 원본을 덮어써도 잃는 것이 없다.
   * true(structural): 되돌릴 수 없는 변환이다. 캔버스는 저장이 버전을 쌓는 구조라 다음 저장이 곧 복제본이지만,
   *   화면은 "이 변환은 되돌릴 수 없으니 저장 전에 확인하라" 고 알려야 한다(needsClone).
   */
  structural: boolean;
  /** 바꿀 것이 있으면 바꾸고 무엇을 바꿨는지 돌려준다. 없으면 null (멱등) */
  run: (doc: { nodes: Node[]; edges: Edge[]; settings?: any }) => string | null;
}

/** 통합 전 구형 노드 타입 → 지금 타입. 변환기·렌더러가 아직 이 이름들을 지원하므로 강제로 바꾸지는 않는다 */
const LEGACY_NODE_TYPES = ['dbExtract', 'csvExtract', 'dbLoad', 'csvLoad', 'processLoad'];

/**
 * 변환 사슬. 각 단계는 문서를 제자리에서 고치고, 바꾼 것이 없으면 null 을 돌려준다.
 *
 * **지금은 비어 있다.** v0(형식 표시가 없던 문서)과 v1 은 구조가 같기 때문이다. v1 은 앞으로의 기준점일 뿐이고,
 * 예전 문서는 다음 저장 때 표시만 붙는다. 구형 노드 타입(dbExtract 등)도 여기서 바꾸지 않는다 —
 * 변환기(canvas_converter.py)와 렌더러가 아직 그대로 지원하고, 바꾸면 예전 빌드에서 그 워크플로를 못 연다.
 *
 * 실제로 깨지는 변경을 할 때 여기에 단계를 더하고 CANVAS_SCHEMA_VERSION 을 올린다. 예:
 *   { to: 2, label: '구형 추출·적재 노드 통합', structural: true,
 *     run: (doc) => { …dbExtract 를 transformLoad 로… ; return '노드 3개를 데이터처리로 바꿨습니다'; } }
 * `run` 은 **모양으로 판정**해야 한다. 버전 필드를 모르는 예전 빌드가 다시 저장하면 최신 문서도 v0 으로
 * 되돌아오므로, 이미 변환된 문서에 다시 돌려도 아무 일이 없어야 한다.
 */
const MIGRATIONS: Migration[] = [];

/** 문서에 구형 노드 타입이 남아 있는가 — 화면 안내용(변환하지는 않는다) */
export function legacyNodeTypesIn(nodes: Node[]): string[] {
  const found = new Set<string>();
  (nodes || []).forEach((n) => { if (n?.type && LEGACY_NODE_TYPES.includes(n.type)) found.add(n.type); });
  return Array.from(found);
}

/**
 * 저장된 문서를 이 빌드가 쓰는 형태로. **원본 객체는 건드리지 않는다.**
 *
 * @param raw 저장소에서 막 읽은 문서 (형식 필드가 없을 수 있다)
 */
export function migrateCanvas(raw: RawCanvasDoc | null | undefined): MigrateResult {
  const src = (raw && typeof raw === 'object') ? raw : {};
  const docVersion = Number(src[SCHEMA_FIELD]) || 0;

  // 이 빌드보다 새 형식 — 추측해서 고치지 않는다. 읽기는 되지만 저장·배포는 막아야 한다.
  if (docVersion > CANVAS_SCHEMA_VERSION) {
    return {
      nodes: Array.isArray(src.nodes) ? src.nodes : [],
      edges: Array.isArray(src.edges) ? src.edges : [],
      settings: src.settings,
      docVersion,
      migrated: false,
      notes: [],
      tooNew: true,
      needsClone: false,
    };
  }

  const notes: string[] = [];

  // ① 모양 보정 — 형식 버전과 무관하게 항상. 손상된 문서를 조용히 비우지 않고 무엇을 했는지 남긴다
  const badNodes = src.nodes !== undefined && !Array.isArray(src.nodes);
  const badEdges = src.edges !== undefined && !Array.isArray(src.edges);
  if (badNodes || badEdges) {
    notes.push(i18n.t('common:schema.damaged', { what: [badNodes && i18n.t('common:schema.nodes'), badEdges && i18n.t('common:schema.edges')].filter(Boolean).join('·') }));
  }

  // 얕은 복사 후 변환 — 호출자가 넘긴 객체를 바꾸지 않는다
  const doc = {
    nodes: Array.isArray(src.nodes) ? [...src.nodes] : [],
    edges: Array.isArray(src.edges) ? [...src.edges] : [],
    settings: src.settings,
  };

  // ② 형식 변환 — 문서 버전보다 뒤에 있는 단계만
  let needsClone = false;
  for (const m of MIGRATIONS) {
    if (docVersion >= m.to) continue;      // 이미 그 단계를 지난 문서
    const note = m.run(doc);               // 모양 기반이라 다시 돌려도 안전하다
    if (note) {
      notes.push(`${m.label}: ${note}`);
      if (m.structural) needsClone = true;
    }
  }

  return { ...doc, docVersion, migrated: notes.length > 0, notes, tooNew: false, needsClone };
}

/**
 * 캔버스 1버전의 내용. `schemaVersion`·`migration` 은 **읽을 때 붙는 정보**이며 저장 페이로드에는 들어가지 않는다
 * (저장은 stampSchema 가 현재 버전을 새로 찍는다).
 */
export interface CanvasDoc {
  nodes: Node[];
  edges: Edge[];
  settings?: any;
  /** 문서에 적혀 있던 형식 버전. 0 이면 형식 표시가 없던 예전 문서 */
  schemaVersion?: number;
  /** 읽으면서 변환한 내역 — 화면이 사용자에게 알리고, tooNew 면 저장·배포를 막는다 */
  migration?: SchemaInfo;
}

/** 화면·상태가 들고 다니는 형식 정보 — migrateCanvas 결과에서 데이터를 뺀 것 */
export interface SchemaInfo {
  docVersion: number;
  migrated: boolean;
  notes: string[];
  tooNew: boolean;
  needsClone: boolean;
}

/** migrateCanvas 결과를 저장소가 돌려줄 CanvasDoc 으로 */
export function toCanvasDoc(m: MigrateResult): CanvasDoc {
  return {
    nodes: m.nodes,
    edges: m.edges,
    settings: m.settings,
    schemaVersion: m.docVersion,
    migration: {
      docVersion: m.docVersion, migrated: m.migrated, notes: m.notes,
      tooNew: m.tooNew, needsClone: m.needsClone,
    },
  };
}

/**
 * 이 빌드보다 새 형식이면 던진다 — **쓰기 경로의 근원에서** 부른다.
 *
 * 화면에서만 막으면 새 쓰기 경로가 생길 때마다 빠뜨린다. 실제로 AI 도우미의 저장·배포 도구가
 * 헤더 버튼을 거치지 않아 차단을 우회했다(2026-09-07 실측). datamodeler modelSchema 가 읽기 자체를
 * 막는 것과 같은 이유다 — 다만 캔버스는 보기만 하는 것이 유용하므로, 읽기는 두고 쓰기를 막는다.
 */
/** 막는 동작 — 문장에 들어갈 말은 common:schema.action.<what> */
export type WriteAction = 'save' | 'deploy' | 'run' | 'duplicate';

export function assertWritable(info: SchemaInfo | null | undefined, what: WriteAction): void {
  if (!info?.tooNew) return;
  throw new Error(i18n.t('common:schema.tooNew', {
    doc: info.docVersion, build: CANVAS_SCHEMA_VERSION, action: i18n.t(`common:schema.action.${what}`),
  }));
}

/** 저장할 때 문서에 실을 형식 필드 */
export function stampSchema<T extends object>(doc: T): T & { [SCHEMA_FIELD]: number } {
  return { ...doc, [SCHEMA_FIELD]: CANVAS_SCHEMA_VERSION };
}
